import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Textarea } from "../../../components/ui/textarea";
import { Label } from "../../../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../../../components/ui/card";
import { useAuth } from "../../../contexts/AuthContext";
import { toast } from "sonner";
import { Home, LogOut, User, Lock, Globe, Mail, Phone, Building2, Briefcase, MapPin, Target, TrendingUp, DollarSign, BarChart3, Bell } from "lucide-react";
import { useLanguage } from "../../../contexts/LanguageContext";
import { LanguageSwitcher } from "../../../components/LanguageSwitcher";
import { CurrencySwitcher } from "../../../components/currency/CurrencySwitcher";

const InvestorSettings = () => {
  const navigate = useNavigate();
  const { user, logout, updateUser, changePassword } = useAuth();
  const { language, t } = useLanguage();
  const [activeSection, setActiveSection] = useState("personal");

  useEffect(() => {
    const hash = window.location.hash.replace("#", "");
    if (hash && ["personal", "password", "notifications", "language", "currency"].includes(hash)) {
      setActiveSection(hash);
    }
  }, []);

  const [personalInfo, setPersonalInfo] = useState({
    name: user?.name || "",
    email: user?.email || "",
    phone: user?.phone || "",
    company: user?.company || "",
    bio: user?.bio || "",
    location: user?.location || "",
    linkedin: user?.linkedin || "",
  });

  const [passwordInfo, setPasswordInfo] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });

  const [investmentInfo, setInvestmentInfo] = useState({
    investmentCapacity: user?.investmentCapacity?.toString() || "",
    preferredSectors: user?.preferredSectors?.join(', ') || "",
    investmentExperience: user?.investmentExperience || "",
    occupation: user?.occupation || "",
  });

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  const handlePersonalInfoChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setPersonalInfo({
      ...personalInfo,
      [e.target.name]: e.target.value,
    });
  };

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPasswordInfo({
      ...passwordInfo,
      [e.target.name]: e.target.value,
    });
  };

  const handleInvestmentInfoChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setInvestmentInfo({
      ...investmentInfo,
      [e.target.name]: e.target.value,
    });
  };

  const handleSavePersonalInfo = async (e: React.FormEvent) => {
    e.preventDefault();
    const success = await updateUser(personalInfo);
    if (success) {
      toast.success(t("settings.personalInfo.success") || "Personal information updated successfully");
    } else {
      toast.error(t("settings.personalInfo.error") || "Failed to update personal information");
    }
  };

  const handleSavePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (passwordInfo.newPassword !== passwordInfo.confirmPassword) {
      toast.error(t("auth.register.passwordMismatch") || "Passwords do not match");
      return;
    }
    const success = await changePassword(passwordInfo.currentPassword, passwordInfo.newPassword);
    if (success) {
      toast.success(t("settings.password.success") || "Password updated successfully");
      setPasswordInfo({
        currentPassword: "",
        newPassword: "",
        confirmPassword: "",
      });
    } else {
      toast.error(t("settings.password.error") || "Failed to update password. Please check your current password.");
    }
  };

  const handleSaveInvestmentInfo = async (e: React.FormEvent) => {
    e.preventDefault();
    const success = await updateUser({
      investmentCapacity: Number(investmentInfo.investmentCapacity),
      preferredSectors: investmentInfo.preferredSectors.split(",").map(s => s.trim()),
      investmentExperience: investmentInfo.investmentExperience,
      occupation: investmentInfo.occupation,
    });
    if (success) {
      toast.success(t("settings.investment.success") || "Investment preferences updated successfully");
    } else {
      toast.error(t("settings.investment.error") || "Failed to update investment preferences");
    }
  };

  return (
    <div className="min-h-screen bg-background" dir={language === "ar" ? "rtl" : "ltr"}>
      {/* Sidebar - Dashboard Style */}
      <aside className="fixed right-0 top-0 h-full w-64 bg-card border-l border-border p-4 hidden lg:block" dir={language === "ar" ? "rtl" : "ltr"}>
        <div className="h-20 mb-6 flex items-center justify-center border-b border-border">
          <h2 className="text-xl font-bold text-primary">{t("auth.register.investor")}</h2>
        </div>

        <nav className="space-y-2">
          <Button
            variant={activeSection === "personal" ? "default" : "ghost"}
            className="w-full justify-start gap-3"
            onClick={() => setActiveSection("personal")}
          >
            <User className="w-4 h-4" />
            {t("settings.personalInfo.title")}
          </Button>
          <Button
            variant={activeSection === "password" ? "default" : "ghost"}
            className="w-full justify-start gap-3"
            onClick={() => setActiveSection("password")}
          >
            <Lock className="w-4 h-4" />
            {t("settings.password.title")}
          </Button>
          <Button
            variant={activeSection === "investment" ? "default" : "ghost"}
            className="w-full justify-start gap-3"
            onClick={() => setActiveSection("investment")}
          >
            <BarChart3 className="w-4 h-4" />
            {t("settings.investment.title")}
          </Button>
          <Button
            variant={activeSection === "notifications" ? "default" : "ghost"}
            className="w-full justify-start gap-3"
            onClick={() => setActiveSection("notifications")}
          >
            <Bell className="w-4 h-4" />
            {t("settings.notifications.title")}
          </Button>
          <Button
            variant={activeSection === "language" ? "default" : "ghost"}
            className="w-full justify-start gap-3"
            onClick={() => setActiveSection("language")}
          >
            <Globe className="w-4 h-4" />
            {t("settings.language.title")}
          </Button>
          <Button
            variant={activeSection === "currency" ? "default" : "ghost"}
            className="w-full justify-start gap-3"
            onClick={() => setActiveSection("currency")}
          >
            <Globe className="w-4 h-4" />
            {t("settings.currency.title")}
          </Button>
        </nav>

        <div className="absolute bottom-4 right-4 left-4 space-y-2">
          <Link to="/dashboard/investor">
            <Button variant="outline" className="w-full justify-start gap-3">
              <Home className="w-4 h-4" />
              {t("common.dashboard")}
            </Button>
          </Link>
          <Button variant="ghost" className="w-full justify-start gap-3 text-destructive hover:text-destructive" onClick={handleLogout}>
            <LogOut className="w-4 h-4" />
            {t("common.logout")}
          </Button>
        </div>
      </aside>

      <main className="lg:mr-64 p-6" dir={language === "ar" ? "rtl" : "ltr"}>
        <div className="max-w-4xl mx-auto space-y-6">
          <div className="flex items-center justify-between mb-8 w-full">
            <div>
              <h1 className="text-2xl font-bold text-foreground">{t("settings.title")}</h1>
              <p className="text-muted-foreground">{t("settings.subtitle")}</p>
            </div>
            <img
              src="/logo.png"
              alt="Knowvest.plus"
              className="h-16 w-auto object-contain"
            />
          </div>
          {/* Personal Information */}
          {activeSection === "personal" && (
            <Card id="personal-info">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <User className="w-5 h-5" />
                  {t("settings.personalInfo.title")}
                </CardTitle>
                <p className="text-sm text-muted-foreground mt-1">
                  {t("settings.personalInfo.subtitle")}
                </p>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSavePersonalInfo} className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="name">{t("settings.personalInfo.fullName")}</Label>
                      <div className="relative">
                        <User className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                        <Input
                          id="name"
                          name="name"
                          value={personalInfo.name}
                          onChange={handlePersonalInfoChange}
                          className="pr-10"
                          placeholder={t("settings.personalInfo.fullName")}
                        />
                      </div>
                    </div>
                    <div>
                      <Label htmlFor="email">{t("settings.personalInfo.email")}</Label>
                      <div className="relative">
                        <Mail className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                        <Input
                          id="email"
                          name="email"
                          type="email"
                          value={personalInfo.email}
                          onChange={handlePersonalInfoChange}
                          className="pr-10 bg-muted cursor-not-allowed"
                          placeholder="example@domain.com"
                          readOnly
                          disabled
                        />
                        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-3 h-3 text-muted-foreground" />
                      </div>
                    </div>
                    <div>
                      <Label htmlFor="phone">{t("settings.personalInfo.phone")}</Label>
                      <div className="relative">
                        <Phone className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                        <Input
                          id="phone"
                          name="phone"
                          value={personalInfo.phone}
                          onChange={handlePersonalInfoChange}
                          className="pr-10 bg-muted cursor-not-allowed"
                          placeholder="+213..."
                          readOnly
                          disabled
                        />
                        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-3 h-3 text-muted-foreground" />
                      </div>
                    </div>
                    <div>
                      <Label htmlFor="company">{t("settings.personalInfo.company")}</Label>
                      <div className="relative">
                        <Building2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                        <Input
                          id="company"
                          name="company"
                          value={personalInfo.company}
                          onChange={handlePersonalInfoChange}
                          className="pr-10"
                          placeholder={t("settings.personalInfo.company")}
                        />
                      </div>
                    </div>
                    <div className="md:col-span-2">
                      <Label htmlFor="location">{t("settings.personalInfo.location")}</Label>
                      <div className="relative">
                        <MapPin className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                        <Input
                          id="location"
                          name="location"
                          value={personalInfo.location}
                          onChange={handlePersonalInfoChange}
                          className="pr-10"
                          placeholder="City, Country"
                        />
                      </div>
                    </div>
                    <div className="md:col-span-2">
                      <Label htmlFor="linkedin">{t("settings.personalInfo.linkedin")}</Label>
                      <div className="relative">
                        <Globe className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                        <Input
                          id="linkedin"
                          name="linkedin"
                          value={personalInfo.linkedin}
                          onChange={handlePersonalInfoChange}
                          className="pr-10"
                          placeholder="LinkedIn profile URL"
                        />
                      </div>
                    </div>
                    <div className="md:col-span-2">
                      <Label htmlFor="bio">{t("settings.personalInfo.aboutYou")}</Label>
                      <Textarea
                        id="bio"
                        name="bio"
                        value={personalInfo.bio}
                        onChange={handlePersonalInfoChange}
                        placeholder={t("settings.personalInfo.aboutYou")}
                        rows={3}
                      />
                    </div>
                  </div>
                  <div className="flex justify-end">
                    <Button type="submit">{t("settings.personalInfo.save")}</Button>
                  </div>
                </form>
              </CardContent>
            </Card>
          )}

          {/* Password Settings */}
          {activeSection === "password" && (
            <Card id="password">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Lock className="w-5 h-5" />
                  {t("settings.password.title")}
                </CardTitle>
                <p className="text-sm text-muted-foreground mt-1">
                  {t("settings.password.subtitle")}
                </p>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSavePassword} className="space-y-4">
                  <div>
                    <Label htmlFor="currentPassword">{t("settings.password.current")}</Label>
                    <div className="relative">
                      <Lock className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <Input
                        id="currentPassword"
                        name="currentPassword"
                        type="password"
                        value={passwordInfo.currentPassword}
                        onChange={handlePasswordChange}
                        className="pr-10"
                        placeholder={t("settings.password.current")}
                      />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="newPassword">{t("settings.password.new")}</Label>
                    <div className="relative">
                      <Lock className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <Input
                        id="newPassword"
                        name="newPassword"
                        type="password"
                        value={passwordInfo.newPassword}
                        onChange={handlePasswordChange}
                        className="pr-10"
                        placeholder={t("settings.password.new")}
                      />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="confirmPassword">{t("settings.password.confirm")}</Label>
                    <div className="relative">
                      <Lock className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <Input
                        id="confirmPassword"
                        name="confirmPassword"
                        type="password"
                        value={passwordInfo.confirmPassword}
                        onChange={handlePasswordChange}
                        className="pr-10"
                        placeholder={t("settings.password.confirm")}
                      />
                    </div>
                  </div>
                  <div className="flex justify-end">
                    <Button type="submit">{t("settings.password.update")}</Button>
                  </div>
                </form>
              </CardContent>
            </Card>
          )}

          {/* Investment Information */}
          {activeSection === "investment" && (
            <Card id="investment-info">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <BarChart3 className="w-5 h-5" />
                  {t("settings.investment.title")}
                </CardTitle>
                <p className="text-sm text-muted-foreground mt-1">
                  {t("settings.investment.subtitle")}
                </p>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSaveInvestmentInfo} className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="investmentCapacity">{t("settings.investment.capacity")}</Label>
                      <div className="relative">
                        <DollarSign className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                        <Input
                          id="investmentCapacity"
                          name="investmentCapacity"
                          type="number"
                          value={investmentInfo.investmentCapacity}
                          onChange={handleInvestmentInfoChange}
                          className="pr-10 bg-muted cursor-not-allowed"
                          placeholder="0.00"
                          readOnly
                          disabled
                        />
                        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-3 h-3 text-muted-foreground" />
                      </div>
                    </div>
                    <div className="md:col-span-2">
                      <Label htmlFor="preferredSectors">{t("settings.investment.preferredSectors")}</Label>
                      <div className="relative">
                        <Target className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                        <Input
                          id="preferredSectors"
                          name="preferredSectors"
                          value={investmentInfo.preferredSectors}
                          onChange={handleInvestmentInfoChange}
                          className="pr-10"
                          placeholder={t("settings.investment.preferredSectors")}
                        />
                      </div>
                    </div>
                    <div className="md:col-span-2">
                      <Label htmlFor="investmentExperience">{t("settings.investment.experience")}</Label>
                      <div className="relative">
                        <TrendingUp className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                        <Input
                          id="investmentExperience"
                          name="investmentExperience"
                          value={investmentInfo.investmentExperience}
                          onChange={handleInvestmentInfoChange}
                          className="pr-10 bg-muted cursor-not-allowed"
                          placeholder={t("settings.investment.experience")}
                          readOnly
                          disabled
                        />
                        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-3 h-3 text-muted-foreground" />
                      </div>
                    </div>
                    <div className="md:col-span-2">
                      <Label htmlFor="occupation">{t("settings.investment.occupation")}</Label>
                      <div className="relative">
                        <Briefcase className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                        <Input
                          id="occupation"
                          name="occupation"
                          value={investmentInfo.occupation}
                          onChange={handleInvestmentInfoChange}
                          className="pr-10"
                          placeholder={t("settings.investment.occupation")}
                        />
                      </div>
                    </div>
                  </div>
                  <div className="flex justify-end">
                    <Button type="submit">{t("settings.investment.update")}</Button>
                  </div>
                </form>
              </CardContent>
            </Card>
          )}

          {/* Notifications */}
          {activeSection === "notifications" && (
            <Card id="notifications">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Globe className="w-5 h-5" />
                  {t("settings.notifications.title")}
                </CardTitle>
                <p className="text-sm text-muted-foreground mt-1">
                  {t("settings.notifications.subtitle")}
                </p>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="font-medium">{t("settings.notifications.email")}</h4>
                      <p className="text-sm text-muted-foreground">{t("settings.notifications.email")}</p>
                    </div>
                    <Button variant="outline" size="sm" asChild>
                      <Link to="/notifications/investor">{t("settings.notifications.manage")}</Link>
                    </Button>
                  </div>
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="font-medium">{t("settings.notifications.investmentUpdates")}</h4>
                      <p className="text-sm text-muted-foreground">{t("settings.notifications.investmentUpdates")}</p>
                    </div>
                    <Button variant="outline" size="sm" asChild>
                      <Link to="/notifications/investor">{t("settings.notifications.manage")}</Link>
                    </Button>
                  </div>
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="font-medium">{t("settings.notifications.marketAlerts")}</h4>
                      <p className="text-sm text-muted-foreground">{t("settings.notifications.marketAlerts")}</p>
                    </div>
                    <Button variant="outline" size="sm" asChild>
                      <Link to="/notifications/investor">{t("settings.notifications.manage")}</Link>
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Language Settings */}
          {activeSection === "language" && (
            <LanguageSwitcher />
          )}

          {/* Currency Settings */}
          {activeSection === "currency" && (
            <CurrencySwitcher />
          )}
        </div>
      </main>
    </div>
  );
};

export default InvestorSettings;